import { Download, Mail } from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  Card,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  Modal,
  Tooltip,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import { pdf, PDFDownloadLink } from "@react-pdf/renderer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import exportInvoiceSheet from "layouts/pages/payroll/actions/exportInvoiceSheet";
import fetchInvoices from "layouts/pages/payroll/actions/fetchInvoices";
import InvoiceEditorPreviewPDF from "layouts/pages/payroll/components/InvoiceEditorTable/components/InvoiceEditorPreviewPDF";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { calculateInvoiceSummary } from "utils/helpers/invoiceHelper";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "40%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const ViewModeToggle = styled(ButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
}));

const SendMultipleInvoicesModal = ({ open, setOpen, selectedIds, setInvoiceFiles }) => {
  const classes = useStyle();

  const [invoiceBlobs, setInvoiceBlobs] = useState([]);
  const [invoiceViewMode, setInvoiceViewMode] = useState("summary");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleExportSheet = async (invoiceId) => {
    const response = await exportInvoiceSheet({ invoiceId });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let filename = "download.xlsx"; // fallback filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        // eslint-disable-next-line prefer-destructuring
        filename = filenameMatch[1];
      }
    }

    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const {
    data: invoiceData,
    isLoading,
    refetch: refetchInvoiceData,
  } = useQuery(
    ["invoicesForSend", selectedIds?.join(";")],
    () =>
      fetchInvoices({
        fetchAll: true,
        filters: {
          _id: selectedIds?.join(";"),
        },
      }),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!selectedIds?.length,
    }
  );

  const getTotalAmount = (row) => {
    const tot =
      (row?.totalHours || 0) * (row?.billRate || 0) +
      (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;

    return `$${tot.toFixed(2)}`;
  };

  const pdfParams = useMemo(() => {
    if (!invoiceData?.data?.length) return [];

    const params = invoiceData.data.map((element) => {
      const invoiceNumber = element?.invoiceNumber
        ? element?.invoiceNumber.toString().padStart(8, "0")
        : null;
      const startDate = element?.startDate;
      const from = {
        name: element?.remittanceInformation?.name,
        address: element?.remittanceInformation?.address,
        city: element?.remittanceInformation?.city,
        state: element?.remittanceInformation?.state,
        zip: element?.remittanceInformation?.zip,
        notes: element?.remittanceInformation?.notes,
        attn: element?.remittanceInformation?.attn,
      };
      const to = {
        name: element?.customerInformation?.name,
        address: element?.customerInformation?.address,
        city: element?.customerInformation?.city,
        state: element?.customerInformation?.state,
        zip: element?.customerInformation?.zip,
        attn: element?.customerInformation?.attn,
      };
      const invoiceInformation = {
        date: element?.invoiceInformation?.date,
        dueDate: element?.invoiceInformation?.dueDate,
        purchaseOrder: element?.invoiceInformation?.purchaseOrder,
      };
      const detailRows = element?.details ?? [];
      const summaryRows = calculateInvoiceSummary({
        details: element?.details ?? [],
        isShiftJob: !!element?.jobSlug,
        eventName: element?.eventName,
        jobName: element?.jobName,
        date: element?.jobSlug
          ? ""
          : moment(element?.eventDate)
              .tz(element?.timeZone || "America/Chicago")
              .format("MM/DD/YYYY"),
      });

      const isVoid = element?.status === "Void";
      const invoiceId = element._id;

      const invoiceFilename = `${invoiceNumber?.toString().padStart(8, "0")}-${(
        element?.eventName ||
        element?.jobName ||
        ""
      ).replace(/\W/g, "_")}-${invoiceViewMode}-${startDate}`;
      const invoiceFilenameReadable = `${invoiceNumber?.toString().padStart(8, "0")} - ${
        element?.eventName || element?.jobName || ""
      } - ${startDate}`;

      let localSubTotal = 0;
      let localTotal = 0;
      // eslint-disable-next-line prefer-const
      let localTax = 0;
      // eslint-disable-next-line prefer-const
      let localDiscount = 0;

      if (invoiceViewMode === "details") {
        detailRows?.forEach((row) => {
          localSubTotal +=
            (row?.totalHours || 0) * (row?.billRate || 0) +
            (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;
        });
      } else {
        summaryRows?.forEach((row) => {
          localSubTotal +=
            (row?.totalHours || 0) * (row?.billRate || 0) +
            (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;
        });
      }

      // localTax = localSubTotal * calculatedBurdenRate;
      localTotal = localSubTotal + localTax;

      const result = {
        invoiceNumber,
        invoiceInformation,
        from,
        to,
        invoiceViewMode,
        detailRows,
        summaryRows,
        subTotal: localSubTotal,
        tax: localTax,
        total: localTotal,
        discount: localDiscount,
        getTotalAmount,
        isShiftJob: !!element?.jobSlug,
        invoiceFilename,
        invoiceFilenameReadable,
        startDate,
        isVoid,
        invoiceId,
      };
      return result;
    });

    return params;
  }, [invoiceData?.data, invoiceViewMode]);

  useEffect(() => {
    if (!pdfParams?.length) return;

    setIsProcessing(true);

    const blobs = [];
    const promises = pdfParams.map(async (element) => {
      const blob = await pdf(<InvoiceEditorPreviewPDF {...element} />).toBlob();
      blobs.push({
        file: blob,
        fileName: element.invoiceFilename,
        subType: element.startDate?.replace(/-/g, "_"),
      });
    });

    Promise.all(promises).then(() => {
      setInvoiceBlobs(blobs);
      setIsProcessing(false);
    });
  }, [invoiceViewMode, pdfParams]);

  const handleDownloadPDF = async (params, filename) => {
    const blob = await pdf(<InvoiceEditorPreviewPDF {...params} />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${params.invoiceFilename}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <Grid
            container
            xs={12}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: 3,
              paddingTop: 1,
            }}
          >
            <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
              Send Multiple Invoices
            </MDTypography>
            <ViewModeToggle variant="contained" size="small">
              <Button
                onClick={() => setInvoiceViewMode("summary")}
                variant={invoiceViewMode === "summary" ? "contained" : "text"}
                size="small"
                sx={{
                  backgroundColor: invoiceViewMode === "summary" ? "white" : "transparent",
                  boxShadow: invoiceViewMode === "summary" ? 1 : 0,
                  color: invoiceViewMode === "summary" ? "white" : "text.secondary",
                }}
              >
                Summary
              </Button>
              <Button
                onClick={() => setInvoiceViewMode("details")}
                variant={invoiceViewMode === "details" ? "contained" : "text"}
                size="small"
                sx={{
                  backgroundColor: invoiceViewMode === "details" ? "white" : "transparent",
                  boxShadow: invoiceViewMode === "details" ? 1 : 0,
                  color: invoiceViewMode === "details" ? "white" : "text.secondary",
                }}
              >
                Details
              </Button>
            </ViewModeToggle>
          </Grid>
          <MDBox p={3}>
            <Grid container xs={12} rowSpacing={3} columnSpacing={2}>
              {isLoading || isProcessing ? (
                <>
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <MDTypography variant="body1" color="dark" sx={{ mt: 2, mb: 1, mr: 2 }}>
                      Preparing invoices
                    </MDTypography>
                    <CircularProgress color="info" />
                  </Grid>
                </>
              ) : (
                <>
                  <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    Files:
                  </MDTypography>
                  <Grid
                    container
                    sx={{ paddingLeft: 2, paddingRight: 2, maxHeight: "40vh", overflowY: "auto" }}
                  >
                    {pdfParams?.map((params) => (
                      <>
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{
                            paddingTop: "0 !important",
                            justifyContent: "space-between",
                            flexWrap: "nowrap",
                            alignItems: "center",
                          }}
                        >
                          <MDTypography variant="body1" color="dark" sx={{ mt: 1, mr: 2 }}>
                            {params.invoiceFilenameReadable}
                          </MDTypography>
                          <Grid item sx={{ alignItems: "center" }}>
                            <Tooltip title="Download PDF">
                              <IconButton
                                onClick={() => handleDownloadPDF(params, params.invoiceFilename)}
                                sx={{ padding: 0, marginLeft: 1 }}
                              >
                                <Download fontSize="small" color="info" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Spreadsheet">
                              <IconButton
                                onClick={() => handleExportSheet(params.invoiceId)}
                                sx={{ padding: 0, marginLeft: 1 }}
                              >
                                <Download fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </>
                    ))}
                  </Grid>
                </>
              )}
            </Grid>
            <Grid container xs={12} mt={2} justifyContent="flex-end">
              <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                <Grid item>
                  <Button
                    color="error"
                    variant="contained"
                    fullWidth
                    onClick={async (e) => {
                      e?.stopPropagation();
                      setOpen(false);
                    }}
                  >
                    <MDTypography variant="body2" color="white">
                      Cancel
                    </MDTypography>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Mail />}
                    onClick={async (e) => {
                      e?.stopPropagation();
                      setInvoiceFiles(invoiceBlobs);
                    }}
                    disabled={isLoading || isProcessing}
                    sx={{
                      backgroundColor: "blue.600",
                      "&:hover": { backgroundColor: "blue.700" },
                    }}
                  >
                    <MDTypography variant="body2" color="white">
                      Send Email
                    </MDTypography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default SendMultipleInvoicesModal;
